import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { AsyncPipe, DecimalPipe, NgClass } from "@angular/common";
import { BehaviorSubject, Subject } from "rxjs";
import { BetPlacementService } from "../../../../../../../../../shared/src/lib/services/bet-placement.service";
import { CoreService } from "../../../../../../../../../shared/src/lib/services/core.service";
import { NavService } from "../../../../../../../../../shared/src/lib/services/nav.service";
import { safeParseFloat } from "../../../../../../../../../shared/src/lib/utils/common";
import { PnlCalculatorService } from "../../../../../../../../../shared/src/lib/services/pnl-calculator.service";
import { ApiService } from "../../../../../../../../../shared/src";
import { isNil } from "lodash";
import { ExposureService } from "../../../../../../../../../shared/src/lib/services/exposure.service";
import { Router } from "@angular/router";
import { MarketOpsService } from "../../market-ops.service";
import { MatButton } from "@angular/material/button";
import { ExRateService } from "../../../../../../../../../shared/src/lib/services/ex-rate.service";
import { FeatureFlagService } from "../../../../../../../../../shared/src/lib/services/feature-flag.service";
import { WhiteLabelConfigService } from "../../../../../../../../../shared/src/lib/services/white-label-config.service";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "[app-m-runner-row]",
  imports: [MatIconModule, NgClass, DecimalPipe, AsyncPipe, MatButton],
  templateUrl: "./m-runner-row.component.html",
  styleUrl: "./m-runner-row.component.scss",
})
export class MRunnerRowComponent implements OnInit, OnDestroy, OnChanges {
  @Input() market: any;
  @Input() data: any;
  @Input() update: any = null;
  @Input() config: any;
  runnerNetEffect$ = new BehaviorSubject<any>(null);
  runnerPNetEffect$ = new BehaviorSubject<any>(null);
  row: any = {
    back1: {
      price: 0,
      size: 0,
    },
    back2: {
      price: 0,
      size: 0,
    },
    back3: {
      price: 0,
      size: 0,
    },
    lay1: {
      price: 0,
      size: 0,
    },
    lay2: {
      price: 0,
      size: 0,
    },
    lay3: {
      price: 0,
      size: 0,
    },
  };
  apiService = inject(ApiService);
  placementService = inject(BetPlacementService);
  coreService = inject(CoreService);
  exRateService = inject(ExRateService);
  navService = inject(NavService);
  pnlCalculator = inject(PnlCalculatorService);
  exposureService = inject(ExposureService);
  router = inject(Router);
  isLineMarket: boolean = false;
  priceChangeFlash: boolean = false;
  isNil = isNil;
  readonly destroy$ = new Subject<void>();
  marketOps = inject(MarketOpsService);
  wlConfigService = inject(WhiteLabelConfigService);
  featureFlagService = inject(FeatureFlagService);
  flashTimeouts: { [key: string]: any } = {};

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.["update"]) {
      const rowUpdate = changes?.["update"]?.currentValue;
      this.handleRowChanges(rowUpdate);
    }
  }

  get isAsianHandicap() {
    return this.market?.description?.bettingType?.includes("ASIAN_HANDICAP");
  }

  get isDoubleChance() {
    return this.market?.description?.marketType === "DOUBLE_CHANCE";
  }

  ngOnInit() {
    this.isLineMarket = this.market?.description?.bettingType === "LINE";
    this.initValues();
    if (this.isLineMarket) {
      this.getLineExposure();
      this.getLNetEffectForExposureCalculation();
    } else {
      this.getNetEffect();
      this.getNetEffectForExposureCalculation();
    }
    this.setFlashingRule();
    this.placementService.changeNotifier$.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      if (type === "slipChange") {
        this.setFlashingRule();
      }
    });
  }

  openGraphWindow() {
    const marketId = this.market?.marketId;
    const selectionId = this.data?.selectionId;
    const url = `${window.location.origin}/market-liquidity/${marketId}/${selectionId}`;
    const win = window.open(url, "_blank", "width=1200,height=600");
  }

  getLineExposure() {
    this.pnlCalculator
      .getLadderTable(this.market?.marketId, "normal", "exposure")
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result?.type === "new") {
          this.runnerPNetEffect$.next(result?.value);
        } else {
          this.runnerPNetEffect$.next(null);
          this.runnerNetEffect$.next(result?.value);
        }
      });
  }

  getNetEffect() {
    this.pnlCalculator
      .getNetEffect(this.market?.marketId, this.data?.selectionId, true, { isDoubleChance: this.isDoubleChance })
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.runnerNetEffect$.next(result);
      });

    this.pnlCalculator
      .getPotentialNetEffect(this.market?.marketId, this.data?.selectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.runnerPNetEffect$.next(result);
      });
  }

  getNetEffectForExposureCalculation() {
    this.pnlCalculator
      .getNetEffect(this.market?.marketId, this.data?.selectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result && result < 0 && this.market?.marketId) {
          this.exposureService.currentMarketExposures[this.market.marketId] = this.exposureService.currentMarketExposures[this.market.marketId]
            ? [...this.exposureService.currentMarketExposures[this.market.marketId], result]
            : [result];
        }
      });

    this.pnlCalculator
      .getPotentialNetEffect(this.market?.marketId, this.data?.selectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result !== null && result < 0 && this.data?.selectionId) {
          this.exposureService.predictedMarketExposure[this.data?.selectionId] = result;
        }
      });
  }

  getLNetEffectForExposureCalculation() {
    this.pnlCalculator
      .getLineNetEffect(this.market?.marketId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result?.maxExposure && result.maxExposure < 0 && this.market?.marketId) {
          this.exposureService.currentMarketExposures[this.market.marketId] = this.exposureService.currentMarketExposures[this.market.marketId]
            ? [...this.exposureService.currentMarketExposures[this.market.marketId], result?.maxExposure]
            : [result?.maxExposure];
        }
      });

    this.pnlCalculator
      .getLinePotentialNetEffect(this.market?.marketId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result?.maxExposure !== null && result?.maxExposure < 0 && this.data?.selectionId) {
          this.exposureService.predictedMarketExposure[this.data?.selectionId] = result?.maxExposure;
        }
      });
  }

  initValues() {
    if (!this.market?.mc?.r) return;

    const initialValues = this.market.mc.r.find((x: any) => {
      if (this.isAsianHandicap) {
        const selectionIdMatches = x?.id === this.data?.selectionId;

        // Normalize hc: if it's null/undefined, treat it as 0
        const xHandicap = x?.ex?.hc ?? 0;
        const expectedHandicap = this.data?.handicap ?? 0;

        return selectionIdMatches && xHandicap === expectedHandicap;
      }

      // For non-Asian markets
      return x?.id === this.data?.selectionId;
    });

    // if (this.isAsianHandicap && this.market?.marketName === "Goal Lines") {
    //   const backPrices = initialValues?.ex?.abp?.map((v: number) => v)?.slice(0, 3);
    //   const layPrices = initialValues?.ex?.alp?.map((v: number) => v)?.slice(0, 3);
    //   console.log(initialValues?.id, initialValues?.ex?.hc, "Back", backPrices, "Lay", layPrices);
    // }

    this.handleRowChanges(initialValues?.ex);
  }

  setFlashingRule() {
    const slipSettings = this.placementService.getSlipSettings();
    this.priceChangeFlash = slipSettings?.betSlipSettings?.flashOnPriceChange === "enable";
  }

  handleRowChanges(updateData: any) {
    if (updateData) {
      this.updateColumn(updateData, 0);
      this.updateColumn(updateData, 1);
      this.updateColumn(updateData, 2);
    }
  }

  updateColumn(updateData: any, i: number) {
    let b = `back${i + 1}`; // back key
    let l = `lay${i + 1}`; // lay key
    let bc = this.row[b]; // back cell
    let lc = this.row[l]; // lay cell

    const nbPrice = safeParseFloat(updateData?.abp?.[i]) ?? 0; // new back price
    const nbSize = safeParseFloat(updateData?.abs?.[i]) ?? 0; // new back size
    const nlPrice = safeParseFloat(updateData?.alp?.[i]) ?? 0; // new lay price
    const nlSize = safeParseFloat(updateData?.als?.[i]) ?? 0; // new lay size

    const bChanged = bc.price !== nbPrice || bc.size !== nbSize; // back changed
    const lChanged = lc.price !== nlPrice || lc.size !== nlSize; // lay changed

    bc.changed = bChanged;
    bc.price = nbPrice;
    bc.size = nbSize;
    if (bChanged) {
      clearTimeout(this.flashTimeouts[b]);
      this.flashTimeouts[b] = setTimeout(() => {
        bc.changed = false;
      }, 2000);
    }

    lc.changed = lChanged;
    lc.price = nlPrice;
    lc.size = nlSize;
    if (lChanged) {
      clearTimeout(this.flashTimeouts[l]);
      this.flashTimeouts[l] = setTimeout(() => {
        lc.changed = false;
      }, 2000);
    }
  }

  handleSelect(_data: any) {
    if (_data?.PRICE === 0) return;
    const isGreyhound = this.market?.eventType?.id === "4339";
    const bettingType = this.market?.description?.bettingType;
    const isToWinTheToss = this.market?.description?.marketType === "TO_WIN_THE_TOSS";
    const context = bettingType?.includes("ASIAN_HANDICAP") ? "asian-handicap" : "match-odds";
    this.navService.openBetSlipBar.next(true);
    const data = {
      ..._data,
      F_ODDS: bettingType === "LINE" ? 2 : _data?.PRICE,
      PROVIDER: "BF",
      PROVIDER_ID: "0",
      SERVICE_ID: "0",
      MARKET_ID: this.market?.marketId,
      SPORT_ID: this.market?.eventType?.id,
      EVENT_ID: this.market?.event?.id,
      PERSISTENCE_TYPE: isGreyhound || isToWinTheToss ? "LAPSE" : this.placementService.persistenceType,
      marketName: this.market?.marketName,
      eventName: this.market?.event?.name,
      SELECTION_ID: this.data?.selectionId,
      HANDICAP: this.data?.handicap,
      runnerName: this.data?.runnerName,
      runners: this.market?.runners,
      context,
      betRunner: this.data,
      isDoubleChance: this.isDoubleChance,
    };
    this.placementService.setParams({
      type: bettingType,
      data,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
